import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  useTheme,
  useScrollTrigger,
  useMediaQuery,
  Typography,
  Box,
  Zoom,
  Fab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  IconButton,
  Menu,
} from "@mui/material";
import { FormatDateOptions, FormattedMessage, useIntl } from "react-intl";
import { isEqual } from "lodash-es";
import { scrollbarWidth } from "@mb-pro-ui/components/table";
import { SnackbarKey, useSnackbar } from "notistack";

import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import ReplayIcon from "@mui/icons-material/Replay";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { useDevice } from "../../utils/DeviceProvider";
import useEditedStyles from "../../hooks/useEditedStyles";
import { useServerCommunication } from "../../utils/ServerCommunicaton";
import { snackbarOptions } from "../snackbar";
import { BeforeSaveErrorType } from "./BasicTemplate";

interface BasicTemplateHeaderProps {
  pageTitle?: string;
  beforeSave?: (currentValue: any) => any;
}

const BasicTemplateHeader = ({ beforeSave }: BasicTemplateHeaderProps) => {
  const [undoDialogOpen, setUndoDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [processedAndErrors, setProcessedAndErrors] = useState<{ processed: any; errors?: BeforeSaveErrorType[] } | null>(null);
  const [activeSyncQueries] = useState(new Set<SnackbarKey>());

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const infoOpen = Boolean(anchorEl);
  const handleInfoClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleInfoClose = () => {
    setAnchorEl(null);
  };

  const { formatMessage, formatDate } = useIntl();
  const theme = useTheme();
  const editedStyle = useEditedStyles();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const smUpMatch = useMediaQuery(theme.breakpoints.up("sm"), { noSsr: true });
  const scrollbarSize = useMemo(() => scrollbarWidth(), []);

  const { saveConfig, waitForConfigSynced } = useServerCommunication();
  const dateFormatOptions: FormatDateOptions = useMemo(
    () => ({
      dateStyle: "short",
      timeStyle: "medium",
    }),
    []
  );

  const {
    device: { currentValue, config, unitid, saving, unitDetails },
    setCurrentValue,
    setSaving,
    setSyncLoading,
    updateConfig,
  } = useDevice();
  const valueEdited = config && !isEqual(currentValue, config?.toolboxConfigOrig ?? config?.toolboxConfig);

  const toolbarRef = useRef<HTMLDivElement>(null);

  const undoChanges = useCallback(() => {
    if (!config?.toolboxConfig) {
      // ha nincs config empty configot állítunk, ennek nem szabadna lefutni.
      throw new Error("TODO: Mi van, ha még nem volt config?");
    }
    setCurrentValue(config.toolboxConfig);
    setUndoDialogOpen(false);
    closeSnackbar();
  }, [config, setCurrentValue, closeSnackbar]);

  const checkSync = useCallback(
    (configId: number, snackKey: SnackbarKey, triesLeft = 10): Promise<void> => {
      if (!activeSyncQueries.has(snackKey)) {
        return Promise.resolve();
      }
      if (triesLeft > 0) {
        return waitForConfigSynced(configId, unitid).then((res) => {
          if (res.success) {
            closeSnackbar(snackKey);
            activeSyncQueries.delete(snackKey);
            enqueueSnackbar(formatMessage({ defaultMessage: "Device notified." }), snackbarOptions({ variant: "success" }, { closeSnackbar }));
            return updateConfig();
          }
          return checkSync(configId, snackKey, triesLeft - 1);
        });
      }
      closeSnackbar(snackKey);
      activeSyncQueries.delete(snackKey);
      enqueueSnackbar(formatMessage({ defaultMessage: "The device has not yet been notified." }), snackbarOptions({ variant: "warning" }, { closeSnackbar }));
      return Promise.resolve();
    },
    [unitid, activeSyncQueries, waitForConfigSynced, updateConfig, closeSnackbar, enqueueSnackbar, formatMessage]
  );

  const doSave = useCallback(
    (processed: any) => {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-restricted-globals
        const confirmRes = confirm("Biztos, hogy menteni akartál?");
        if (!confirmRes) {
          return;
        }
      }
      setSaving(true);
      setSyncLoading(true);

      saveConfig(unitid, processed)
        .then((res) => {
          updateConfig(true);
          return res;
        })
        .then((res) => {
          enqueueSnackbar(formatMessage({ defaultMessage: "Config saved to the database" }), snackbarOptions({ variant: "success" }, { closeSnackbar }));
          return res;
        })
        .then((res) => {
          if (!res.id) {
            return;
          }
          setSaving(false);

          const snackKey = enqueueSnackbar(
            formatMessage({ defaultMessage: "Sync check in progress" }),
            snackbarOptions(
              {
                key: `check-snack-${res.id}`,
                variant: "info",
                autoHideDuration: null,
              },
              {
                customActionLabel: formatMessage({ defaultMessage: "Stop" }),
                customAction: (key: SnackbarKey) => {
                  setSyncLoading(false);
                  activeSyncQueries.delete(key);
                  closeSnackbar(key);
                },
              }
            )
          );
          activeSyncQueries.add(snackKey);
          return checkSync(res.id, snackKey);
        })
        .catch((err) => {
          if (err.status === 404) {
            enqueueSnackbar(
              formatMessage({ defaultMessage: "You don't have permission to configure this device." }),
              snackbarOptions({ variant: "error", autoHideDuration: null }, { closeSnackbar })
            );
          } else {
            enqueueSnackbar(
              formatMessage({ defaultMessage: "Config saving failed." }),
              snackbarOptions({ variant: "error", autoHideDuration: null }, { closeSnackbar })
            );
          }
          console.error(err);
        })
        .finally(() => {
          setSaving(false);
          setSyncLoading(false);
        });
    },
    [activeSyncQueries, closeSnackbar, enqueueSnackbar, formatMessage, saveConfig, setSaving, setSyncLoading, unitid, updateConfig, checkSync]
  );

  const onSave = useCallback(
    (currentValue: any) => {
      const { processed, errors } = beforeSave ? beforeSave(currentValue) : { processed: currentValue, errors: undefined };
      if (!processed) {
        return;
      }

      if (errors) {
        setProcessedAndErrors({ processed, errors });
        setErrorDialogOpen(true);
      } else {
        setProcessedAndErrors(null);
        doSave(processed);
      }
    },
    [beforeSave, doSave]
  );

  useEffect(() => {
    return () => {
      activeSyncQueries.clear();
      closeSnackbar();
    };
  }, [closeSnackbar, activeSyncQueries]);

  const saveFab = useMemo(
    () => (
      <ScrollToShow target={toolbarRef.current?.parentNode || undefined} threshold={40}>
        <Fab
          disabled={saving}
          onClick={() => onSave(currentValue)}
          style={{
            position: "fixed",
            top: `${(smUpMatch ? 64 : 58) + parseInt(theme.spacing(smUpMatch ? 2 : 1.5), 10)}px`,
            right: `${scrollbarSize + parseInt(theme.spacing(smUpMatch ? 2 : 1.5), 10)}px`,
          }}
          className={editedStyle.withHover}
        >
          <SaveOutlinedIcon />
        </Fab>
      </ScrollToShow>
    ),
    [currentValue, onSave, saving, smUpMatch, scrollbarSize, theme, editedStyle]
  );

  const undoDialog = useMemo(
    () => (
      <Dialog open={undoDialogOpen} onClose={() => setUndoDialogOpen(false)}>
        <DialogTitle>{formatMessage({ defaultMessage: "Undo all changes" })}</DialogTitle>
        <DialogContent>
          {!config?.toolboxConfigOrig || isEqual(config?.toolboxConfig, config?.toolboxConfigOrig) || !isEqual(config?.toolboxConfig, currentValue) ? (
            <DialogContentText>{formatMessage({ defaultMessage: "Are you sure you want to undo all changes?" })}</DialogContentText>
          ) : null}
          {config?.toolboxConfigOrig && !isEqual(config?.toolboxConfig, config?.toolboxConfigOrig) ? (
            isEqual(config?.toolboxConfig, currentValue) ? (
              <DialogContentText sx={{ color: (theme) => theme.palette.warning.main }}>
                {formatMessage({ defaultMessage: "The device has some new configuration fields. We initialized those to their default value." })}
              </DialogContentText>
            ) : (
              <DialogContentText sx={{ color: (theme) => theme.palette.warning.main }}>
                {formatMessage({
                  defaultMessage: "The device has some new configuration fields. Those will be set to their default value.",
                })}
              </DialogContentText>
            )
          ) : null}
        </DialogContent>
        <DialogActions>
          {config?.toolboxConfigOrig && !isEqual(config?.toolboxConfig, config?.toolboxConfigOrig) && isEqual(config?.toolboxConfig, currentValue) ? (
            <Button onClick={() => setUndoDialogOpen(false)}>{formatMessage({ defaultMessage: "OK" })}</Button>
          ) : (
            <>
              <Button onClick={() => setUndoDialogOpen(false)}>{formatMessage({ defaultMessage: "Cancel" })}</Button>
              <Button onClick={undoChanges}>{formatMessage({ defaultMessage: "Yes, undo all" })}</Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    ),
    [undoDialogOpen, formatMessage, config?.toolboxConfig, config?.toolboxConfigOrig, currentValue, undoChanges]
  );

  const errorDialog = useMemo(() => {
    if (!processedAndErrors?.errors) {
      return null;
    }
    const { errors, processed } = processedAndErrors;
    return (
      <Dialog open={errorDialogOpen} onClose={() => setUndoDialogOpen(false)}>
        <DialogTitle>{formatMessage({ defaultMessage: "Errors" })}</DialogTitle>
        <DialogContent>
          {errors.map((error, i) => (
            <Box key={`error-${i}`}>
              <Typography variant="h6" color="error">
                {error.title}
              </Typography>
              {error.details && (
                <ul style={{ margin: 0 }}>
                  {error.details.map((detail, j) => (
                    <Typography component="li" key={`error-detail-${i}-${j}`} color="error">
                      {detail}
                    </Typography>
                  ))}
                </ul>
              )}
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorDialogOpen(false)}>{formatMessage({ defaultMessage: "Cancel" })}</Button>
          <Button
            onClick={() => {
              setErrorDialogOpen(false);
              doSave(processed);
            }}
          >
            {formatMessage({ defaultMessage: "Save with errors" })}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }, [processedAndErrors, errorDialogOpen, formatMessage, doSave]);

  const headerInfo = useMemo(() => {
    const info: { title: string; value: string }[] = [];

    if (currentValue?.accountinfo?.name) {
      info.push({ title: formatMessage({ defaultMessage: "Name" }), value: currentValue.accountinfo.name });
    }
    if (unitDetails?.phonenumber) {
      info.push({ title: formatMessage({ defaultMessage: "Phone number" }), value: unitDetails.phonenumber });
    }
    if (unitDetails) {
      info.push({
        title: formatMessage({ defaultMessage: "Hardware type" }),
        value: `${unitDetails.hwtype} (v${unitDetails.hwver}, sw: v${unitDetails.swver})`,
      });
      info.push({ title: formatMessage({ defaultMessage: "Owner company" }), value: unitDetails.company_name ?? "-" });
    }
    if (unitDetails?.last_inmsg) {
      info.push({
        title: formatMessage({ defaultMessage: "Last communication" }),
        value: formatDate(new Date(unitDetails.last_inmsg), dateFormatOptions),
      });
    }

    return info;
  }, [currentValue, formatDate, formatMessage, unitDetails, dateFormatOptions]);

  return (
    <>
      <Grid ref={toolbarRef} container spacing={1} sx={{ mb: 2, mt: 0.5, alignItems: "center", flexDirection: "row" }} alignItems="center">
        <Grid item>
          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", ml: 0.5 }}>
            <Typography variant="subtitle2" color="secondary.main">
              {unitid}
            </Typography>
            {headerInfo.length > 0 ? (
              <>
                <IconButton onClick={handleInfoClick} sx={{ color: (theme) => theme.palette.text.secondary, ml: 0.5 }}>
                  <InfoOutlinedIcon sx={{ height: 20, width: 20 }} />
                </IconButton>
                <Menu anchorEl={anchorEl} open={infoOpen} onClose={handleInfoClose}>
                  <Box sx={{ minWidth: 250, maxWidth: "min(400px, 100%)", px: 1 }}>
                    {headerInfo.map(({ title, value }, index) => (
                      <Box key={`header-info-${index}`}>
                        <Typography variant="caption" color="secondary">
                          {title}
                        </Typography>
                        <Typography variant="body2">{value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Menu>
              </>
            ) : null}
          </Box>
        </Grid>

        <Grid item sx={{ flexGrow: 1 }}></Grid>
        {valueEdited && (
          <Grid item>
            <Button color="inherit" startIcon={<ReplayIcon />} onClick={() => setUndoDialogOpen(true)}>
              <FormattedMessage defaultMessage="Restore" />
            </Button>
          </Grid>
        )}
        <Grid item>
          <Button
            color="inherit"
            startIcon={<SaveOutlinedIcon />}
            sx={{ ml: valueEdited ? 1 : 2 }}
            className={valueEdited ? editedStyle.withHover : ""}
            disabled={saving}
            onClick={() => onSave(currentValue)}
          >
            <FormattedMessage defaultMessage="Save" />
          </Button>
        </Grid>
      </Grid>
      {valueEdited && saveFab}
      {undoDialog}
      {errorDialog}
    </>
  );
};

const ScrollToShow = (props: { target?: Node; disableHysteresis?: boolean; threshold?: number; children: ReactElement }) => {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: props.threshold,
    target: props.target,
  });

  return <Zoom in={trigger}>{props.children}</Zoom>;
};

export default BasicTemplateHeader;
